import React, { useEffect, useContext, useState } from "react"
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { useTranslation } from 'react-i18next';
import { Box, Typography, IconButton, Divider } from "@mui/material"
import TagButton from "../TagButton"
import { addTour } from '../../app/features/cartSlice'
import { attractionsEqual } from '../../utils/activity'
import { useDispatch, useSelector } from "react-redux"
import dayjs from "dayjs"
import { useNavigate } from "react-router-dom"
import CheckoutContext from '../../components/CheckoutContext/checkoutContext'
import { prePrareTourBookingData } from "../../utils/checkout"
import { selectTotalPrice, selectCartItems } from '../../app/features/cartSlice';
import { formatPrice } from '../../utils/price';

const sumNonAttractionPricedOptions = (opts = []) => {
    if (!Array.isArray(opts)) return 0;
    return opts.reduce((sum, option) => {
        const source = String(option?.source || option?.type || '').toLowerCase();
        if (source === 'attraction') return sum;
        return sum + (Number(option?.price) || 0);
    }, 0);
};

const PaymentSummary = ({
    items,
    iva = 0,
    tour,
    extras,
    selectedHour = null,
    date,
    tourBooking,
    onAddedToCart,
    showPersonalizeButton = true,
    // control whether the summary can be expanded/collapsed
    expandable = true,
    // whether to show the expand/collapse button in header
    showExpandButton = true,
    // default expanded state
    defaultExpanded = true,
}) => {

    const { t } = useTranslation(['tours', 'paths', 'components']);
    const [expanded, setExpanded] = useState(Boolean(defaultExpanded));
    const [totalPrice, setTotalPrice] = React.useState(0);
    const navigate = useNavigate();
    const checkoutContext = useContext(CheckoutContext);
    // const currency = tour?.country?.currency || '€';
    const [currency, setCurrency] = useState('');

    // prefer options.selectedAttractions (canonical) > extras.selectedAttractions > extras.attractions > extras.activities
    const extrasAttractions = Array.isArray(tour?.options?.selectedAttractions)
        ? tour.options.selectedAttractions
        : (Array.isArray(extras?.selectedAttractions) ? extras.selectedAttractions : (Array.isArray(extras?.attractions) ? extras.attractions : (Array.isArray(extras?.activities) ? extras.activities : [])));

    // const selectedTotalPrice = useSelector(selectTotalPrice) || Math.ceil((totalPrice + (tour?.tax > 0 ? tour?.tax : 1 * totalPrice)));

    useEffect(() => {
        // compute items subtotal
        const subTotal = items.reduce((total, item) => total + (Number(item.price || 0) * Number(item.value || 0)), 0);

        // compute extras attractions total (support legacy extras.activities)
        let totalByActivity = 0;
        if (extrasAttractions.length > 0) {
            extrasAttractions.forEach(activity => {
                (activity?.rates || []).forEach(rate => {
                    const pax = items?.find(pax => pax.title === rate.pax || rate.pax?.includes?.(pax.title));
                    if (pax) totalByActivity += Number(rate.price || 0) * Number(pax.value || 0);
                });
            });
        }

        // Collect options cost.
        // Prefer pricedOptions (same source used by cart selectors) and exclude attraction to avoid double count.
        let optionsCost = 0;
        const pricedOptions = Array.isArray(tour?.pricedOptions)
            ? tour.pricedOptions
            : (Array.isArray(tour?.customizations?.pricedOptions) ? tour.customizations.pricedOptions : []);

        if (pricedOptions.length > 0) {
            optionsCost = sumNonAttractionPricedOptions(pricedOptions);
        } else {
            // Fallback for transient UI states before pricedOptions are built.
            const vals = [];
            const paxCount = items.reduce((s, it) => s + (Number(it.value || 0) || 0), 0) || 1;

            if (tour?.customizations) {
                const c = tour.customizations;
                if (c.travelCompanion && Number(c.travelCompanion.price)) vals.push(Number(c.travelCompanion.price));
                if (c.language && Number(c.language.price)) vals.push(Number(c.language.price));
                if (c.guide && Number(c.guide.price)) vals.push(Number(c.guide.price));
                if (c.vehicle && Number(c.vehicle.price)) vals.push(Number(c.vehicle.price));
                if (c.vehicle && c.vehicle.vehicle_option && Number(c.vehicle.vehicle_option.value)) vals.push(Number(c.vehicle.vehicle_option.value));
                if (Array.isArray(c.custom)) c.custom.forEach(cc => { const p = Number(cc.price || 0); if (p > 0) vals.push(Number(p * paxCount)); });
                if (Array.isArray(c.food)) {
                    c.food.forEach(f => {
                        const price = Number(f.price || 0) || 0;
                        const factor = Number(f.num_pax || 1) || 1;
                        if (price > 0) vals.push(Number(price * paxCount * factor));
                    });
                }
            }

            if (tour?.options) {
                const o = tour.options;
                if (o.selectedCompanion && Number(o.selectedCompanion.price)) vals.push(Number(o.selectedCompanion.price));
                if (Array.isArray(o.languages)) o.languages.forEach(l => { if (l && Number(l.price)) vals.push(Number(l.price)); });
                if (o.selectedGuide && Number(o.selectedGuide.price)) vals.push(Number(o.selectedGuide.price));
                if (o.selectedVehicle && Number(o.selectedVehicle.price)) vals.push(Number(o.selectedVehicle.price));
                if (o.selectedVehicle && o.selectedVehicle.vehicle_option && Number(o.selectedVehicle.vehicle_option.value)) vals.push(Number(o.selectedVehicle.vehicle_option.value));
                if (Array.isArray(o.selectedFood)) o.selectedFood.forEach(f => {
                    const price = Number(f.price || 0) || 0;
                    const factor = Number(f.num_pax || 1) || 1;
                    if (price > 0) vals.push(Number(price * paxCount * factor));
                });
            }

            optionsCost = vals.reduce((s, n) => s + (Number(n) || 0), 0);
        }

        const itemTotal = subTotal + totalByActivity + optionsCost;
        // const booking = tour?.tax > 0 ? itemTotal + Math.ceil(itemTotal * tour?.tax) : itemTotal;
        const booking = itemTotal;

        setTotalPrice(booking);
        setCurrency(tour?.currency?.symbol || tour?.tourDetails?.currency?.symbol || '€');
    }, [items, extras, tour?.customizations, tour?.options, tour?.tax, tourBooking]);


    const dispatch = useDispatch();
    const checkoutTours = useSelector(selectCartItems);


    const addToCart = () => {
        const currentSelectedHour =
            selectedHour ??
            tour?.options?.selectedHour ??
            tour?.customizations?.schedule?.selectedHour ??
            tour?.customizations?.schedule ??
            tour?.selectedHour ??
            null;

        // If the tour is already in the cart, prefer opening the editor with the
        // existing cart entry so the modal reflects exactly what's saved.
        const existing = checkoutTours?.find(ct => ct.slug === tour?.slug);
        if (existing) {
            // If the page passed extras.activities and they differ from what's
            // already in the cart, update the cart entry instead of just opening the editor.
            const existingActivities = existing.options?.selectedAttractions ?? existing.extras?.attractions ?? existing.extras?.activities ?? existing.customizations?.attractions ?? [];
            const newActivities = Array.isArray(tour?.options?.selectedAttractions) ? tour.options.selectedAttractions : (Array.isArray(extras?.selectedAttractions) ? extras.selectedAttractions : (Array.isArray(extras?.attractions) ? extras.attractions : (Array.isArray(extras?.activities) ? extras.activities : [])));
            const existingSelectedHour =
                existing?.options?.selectedHour ??
                existing?.customizations?.schedule?.selectedHour ??
                existing?.customizations?.schedule ??
                existing?.selectedHour ??
                null;
            const scheduleChanged = Boolean(currentSelectedHour) && currentSelectedHour !== existingSelectedHour;

            // use shared helper to compare attractions lists

            if (!attractionsEqual(existingActivities, newActivities) || scheduleChanged) {
                try {
                    const tourBookingUpd = prePrareTourBookingData(
                        existing,
                        date,
                        items,
                        null,
                        {
                            selectedAttractions: newActivities,
                            availabilityHours: existing?.options?.availabilityHours ?? existing?.availabilityHours,
                            selectedHour: currentSelectedHour ?? existingSelectedHour
                        }
                    );
                    dispatch(addTour(tourBookingUpd));
                    // notify parent/editor with updated booking so UI can reflect changes
                    if (onAddedToCart && typeof onAddedToCart === 'function') {
                        try { onAddedToCart(tourBookingUpd); } catch (e) { console.warn('onAddedToCart threw', e); }
                    }
                    if (checkoutContext && typeof checkoutContext.handleEditTour === 'function') {
                        setTimeout(() => {
                            try { checkoutContext.handleEditTour(tourBookingUpd.slug); } catch (e) { console.warn('Failed to open editor after setExtraActivities', e); }
                        }, 50);
                    }
                } catch (e) {
                    console.warn('Failed to update existing cart entry with new extras', e);
                }

                return;
            }

            // No extras changes to apply - fall back to previous behavior (open editor)
            if (onAddedToCart && typeof onAddedToCart === 'function') {
                try {
                    onAddedToCart(existing);
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.warn('onAddedToCart threw', e);
                }
            }

            if (checkoutContext && typeof checkoutContext.handleEditTour === 'function') {
                setTimeout(() => {
                    try {
                        checkoutContext.handleEditTour(existing.slug);
                    } catch (e) {
                        // eslint-disable-next-line no-console
                        console.warn('Failed to open editor after addToCart via context', e);
                    }
                }, 50);
            }

            return;
        }

        // prepare tour object to add to cart (not yet present)
        const selectedAttractionsFromProps = Array.isArray(tour?.options?.selectedAttractions) ? tour.options.selectedAttractions : (Array.isArray(extras?.selectedAttractions) ? extras.selectedAttractions : (Array.isArray(extras?.attractions) ? extras.attractions : (Array.isArray(extras?.activities) ? extras.activities : [])));
        const tourtoBookv2 = prePrareTourBookingData(
            tour,
            date,
            items,
            null,
            {
                selectedAttractions: selectedAttractionsFromProps,
                availabilityHours: tour?.options?.availabilityHours ?? tour?.availabilityHours,
                selectedHour: currentSelectedHour
            }
        );
        dispatch(addTour(tourtoBookv2));

        // First, if parent provided an onAddedToCart, call it so the page can open the editor in-place
        if (onAddedToCart && typeof onAddedToCart === 'function') {
            try {
                onAddedToCart(tourtoBookv2);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.warn('onAddedToCart threw', e);
            }
        }

        // Fallback: also try using CheckoutContext.handleEditTour if available (keeps previous behavior)
        if (checkoutContext && typeof checkoutContext.handleEditTour === 'function') {
            setTimeout(() => {
                try {
                    checkoutContext.handleEditTour(tourtoBookv2.slug);
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.warn('Failed to open editor after addToCart via context', e);
                }
            }, 50);
        }
    }

    // keep expanded in sync with `expandable` changes: if not expandable, force collapsed
    useEffect(() => {
        if (!expandable) setExpanded(false);
        else setExpanded(Boolean(defaultExpanded));
    }, [expandable, defaultExpanded]);

    return (
        <Box sx={{
            backgroundColor: '#F0F0F1',
            borderRadius: "8px",
            p: "0.5rem 1rem 1rem 1rem",
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
        }}>
            <Box sx={{ borderBottom: '1px solid', borderColor: 'secondary.main', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: expandable ? 'pointer' : 'default' }} onClick={() => { if (expandable) setExpanded(!expanded); }}>
                    <Typography color="secondary" variant="h5" sx={{ mr: 1 }}>
                        {t('components:ordersummary')}
                    </Typography>
                    <Divider orientation="horizontal" flexItem sx={{ borderColor: 'secondary.main' }} />
                </Box>
                <Box>
                    {showExpandButton && (
                        <IconButton onClick={() => { if (expandable) setExpanded(!expanded); }} size="small" aria-label={expanded ? 'collapse' : 'expand'}>
                            {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                    )}
                </Box>
            </Box>

            {expanded && (
                <Box sx={{ flexGrow: 1 }}>
                    {/* Render booking options (customizations and options) as name + price */}
                    {(() => {
                        const opts = [];
                        const normalizeLabel = (o) => (o?.label || o?.name || o?.title || o?.vehicle_type || o?.type_guide || '').toString().trim();

                        // customizations first
                        if (tour?.customizations) {
                            const c = tour.customizations;
                            if (c.travelCompanion) opts.push(c.travelCompanion);
                            if (c.language) opts.push(c.language);
                            if (c.guide) opts.push(c.guide);
                            if (c.vehicle) opts.push(c.vehicle);
                            // include custom extras list if present
                            if (c.custom && Array.isArray(c.custom)) opts.push(...c.custom);
                            if (c.attractions && Array.isArray(c.attractions)) opts.push(...c.attractions);
                            if (c.food && Array.isArray(c.food)) opts.push(...c.food);
                        }

                        // also check tour.options for any selected primitives
                        if (tour?.options) {
                            const o = tour.options;
                            if (o.selectedCompanion) opts.push(o.selectedCompanion);
                            if (Array.isArray(o.languages)) opts.push(...o.languages);
                            if (o.selectedGuide) opts.push(o.selectedGuide);
                            if (o.selectedVehicle) opts.push(o.selectedVehicle);
                            // include selected custom extras from options if provided
                            if (o.selectedCustom && Array.isArray(o.selectedCustom)) opts.push(...o.selectedCustom);
                            if (o.selectedAttractions && Array.isArray(o.selectedAttractions)) opts.push(...o.selectedAttractions);
                            if (o.selectedFood && Array.isArray(o.selectedFood)) opts.push(...o.selectedFood);
                        }

                        // normalize and dedupe options by normalized label, and skip attractions that are also present in extras.activities
                        const map = new Map();
                        opts.forEach(o => {
                            const label = normalizeLabel(o) || String(o || '');
                            // for food items, price may need to be multiplied by pax count and num_pax
                            let price = Number(o?.price ?? o?.rate ?? (o?.vehicle_option && o.vehicle_option.value) ?? 0) || 0;
                            if (o && (o.description || o.num_pax)) {
                                // assume it's a food-like object
                                const factor = Number(o?.num_pax || 1) || 1;
                                const paxCountForRender = items.reduce((s, it) => s + (Number(it.value || 0) || 0), 0) || 1;
                                price = Number(price * paxCountForRender * factor) || price;
                            }
                            const key = label.toLowerCase();

                            // skip if attraction present in extras (compare normalized)
                            const isAttraction = o && (o.type === 'attraction' || o.source === 'attraction' || (!!o?.name && !!o?.price && o?.id));
                            const inExtras = extrasAttractions.some(act => normalizeLabel(act).toLowerCase() === key);
                            if (isAttraction && inExtras) return;

                            if (price > 0 && !map.has(key)) map.set(key, { label: label, price });
                        });

                        const pricedOpts = Array.from(map.values());

                        return pricedOpts.length > 0 ? (
                            <>
                                {pricedOpts.map((opt, idx) => (
                                    <Box key={`opt-${idx}`} sx={{ display: 'flex', justifyContent: 'space-between', mb: '.5rem' }}>
                                        <Typography color="secondary" sx={{ fontSize: 13 }}>{opt.label}</Typography>
                                        <Typography color="secondary" sx={{ fontSize: 13 }}>{formatPrice(opt.price)} {currency}</Typography>
                                    </Box>
                                ))}
                            </>
                        ) : null;
                    })()}
                    {items.map((item, itemIndex) => (
                        <Box
                            key={`${item.title}-${itemIndex}`}
                            sx={{
                                alignItems: "center",
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                                mb: ".5rem",
                            }}
                        >
                            <Typography
                                color="secondary"
                                sx={{ fontSize: 14, fontFamily: "inter" }}
                            >
                                {item.title}
                            </Typography>
                            <Typography
                                color="secondary"
                                sx={{ fontSize: 14, fontFamily: "inter" }}
                            >
                                {formatPrice(item.price * item.value)} {currency}
                            </Typography>
                        </Box>
                    ))}

                    {
                        extrasAttractions.map((extra, extraIndex) => {
                            // Compute total for this activity by summing its rates against items
                            const rates = Array.isArray(extra?.rates) ? extra.rates : (extra?.rates ? [extra.rates] : []);
                            let totalExtra = 0;
                            rates.forEach(rate => {
                                const pax = items?.find(pax => pax.title === rate.pax || rate.pax?.includes?.(pax.title));
                                if (pax) totalExtra += Number(rate.price || 0) * Number(pax.value || 0);
                            });
                            if (totalExtra <= 0) return null;

                            return (
                                <Box key={`${extra.name}-${extraIndex}`} sx={{ pl: '1rem', mb: '.5rem', display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography color="secondary" sx={{ fontSize: 12, fontFamily: 'inter' }}>{extra.name}</Typography>
                                    <Typography color="secondary" sx={{ fontSize: 12, fontFamily: 'inter' }}>{formatPrice(totalExtra)} {currency}</Typography>
                                </Box>
                            );
                        })}
                </Box>
            )}

            <Box sx={{ mt: 'auto' }}>
                {/* Total row always visible */}
                <Box sx={{
                    alignItems: "center",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    mt: "1.5rem",
                }}>
                    <Typography color="secondary" sx={{ lineHeight: 1 }}>
                        Total:
                    </Typography>
                    <Typography color="secondary" variant="h5" sx={{ lineHeight: 1 }}>
                        {formatPrice(totalPrice)} {currency}
                    </Typography>
                </Box>

                {/* show IVA and personalize button only when expanded */}
                {expanded && (
                    <>
                        <Typography
                            color="grayText"
                            sx={{
                                fontSize: 12,
                                fontFamily: "inter",
                                textAlign: "right",
                                mb: "0.5rem",
                            }}
                        >
                            {tour?.tax > 0 ? t('IVANotincluded') : ''}
                        </Typography>
                        {showPersonalizeButton && (
                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                <TagButton
                                    text={t('components:booking-button')}
                                    onClick={addToCart}
                                    id='personalizar-btn'
                                    sx={{
                                        borderRadius: "2rem",
                                        px: "1.5rem",
                                        fontSize: 15,
                                        fontFamily: 'Poppins',
                                        fontWeight: 600,
                                        lineHeight: 1.5,
                                        py: { xs: "1rem", md: "10px" },
                                    }}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    )
}

export default PaymentSummary
